import './style.scss';

import { useAnimationsEnabled } from 'providers/Animation';
import { LanguageContext } from 'providers/Language';
import { TrackingContext } from 'providers/Tracking';
import TRACKING_EVENT_COLLECTIONS from 'providers/Tracking/trackingEventCollections';
import { useCallback, useContext, useEffect, useRef } from 'react';

export default function AnimationToggle() {
  const { areAnimationsEnabled, setAreAnimationsEnabled } = useAnimationsEnabled();
  const { t } = useContext(LanguageContext);
  const { trackEventDataLayerService } = useContext(TrackingContext);

  const inputRef = useRef<HTMLInputElement>(null);

  const trackToggle = useCallback(
    (isEnabled: boolean) => {
      trackEventDataLayerService(
        TRACKING_EVENT_COLLECTIONS.FLYER_ANIMATION_TOGGLE_CLICKED,

        {
          contentLabel: t('animationControlButton'),
        },
        {
          eventAction: 'Flyer_Toggle',
          eventInteractionType: isEnabled ? 'on' : 'off',
        },
      );
    },
    [t, trackEventDataLayerService],
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [areAnimationsEnabled]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setAreAnimationsEnabled(checked);
      trackToggle(checked);
    },
    [setAreAnimationsEnabled, trackToggle],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setAreAnimationsEnabled((prev) => {
          const newValue = !prev;
          trackToggle(newValue);
          return newValue;
        });
      }
    },
    [setAreAnimationsEnabled, trackToggle],
  );

  return (
    <span className="animation-toggle">
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label htmlFor="animation-toggle-input" className="animation-toggle__label">
        <input
          type="checkbox"
          role="switch"
          id="animation-toggle-input"
          className="animation-toggle__input"
          onChange={handleChange}
          checked={areAnimationsEnabled}
          onKeyDown={handleKeyDown}
          ref={inputRef}
        />
        <span className="animation-toggle__label-content">
          <span className="animation-toggle__label-text">{t('animationControlButton')}</span>
        </span>
      </label>
    </span>
  );
}
